import type { AuthUser } from '../types/auth'

/**
 * IDs de centro de custo do usuário: UNIÃO do CC primário (`cost_center_id`)
 * com os secundários de `cost_center_all_ids`. Dedup, ignora NaN.
 *
 * O primário entra SEMPRE, não só quando `all_ids` está vazio — e nada garante
 * que ele esteja na lista: `EmployeesService` grava `cost_center_all_ids` direto
 * do DTO, sem juntar o `cost_center_id`. Com fallback em vez de união, gerente
 * com secundários cadastrados e o próprio CC fora da lista perdia acesso ao
 * próprio centro de custo — e, na Sidebar, deixava de ser tratado como CC
 * restrito, revelando rotas que deveriam ficar escondidas.
 *
 * Espelha `extractCostCenterIds` do backend
 * (`entities/employees/process-scope.util.ts`), que já passou por esse mesmo bug
 * e o corrigiu nesta direção. As duas implementações precisam concordar: é o
 * mesmo conceito de escopo dos dois lados.
 */
export function getUserCostCenterIds(
  user: Pick<AuthUser, 'cost_center_id' | 'cost_center_all_ids'> | null | undefined,
): number[] {
  const ids: number[] = []

  if (user?.cost_center_id) {
    ids.push(Number(user.cost_center_id))
  }

  if (user?.cost_center_all_ids) {
    user.cost_center_all_ids
      .split(',')
      .map((id) => parseInt(id.trim(), 10))
      .filter((id) => !isNaN(id))
      .forEach((id) => ids.push(id))
  }

  return Array.from(new Set(ids))
}

/**
 * Empresas de escopo do usuário: todos os `member_company_ids`. O tipo de
 * cliente já é recortado por capabilities (ADR-028), então não há mais
 * subtração da empresa "externa".
 *
 * Espelha `extractOperationalCompanyIds` do backend
 * (`entities/employees/process-scope.util.ts`).
 */
export function getUserCompanyIds(
  user: Pick<AuthUser, 'member_company_ids'> | null | undefined,
): number[] {
  return Array.from(
    new Set((user?.member_company_ids ?? []).map(Number).filter((id) => !isNaN(id)))
  )
}
